/**
 * Bausteine und Regelwerk der Baustelleneinrichtung (`handbuecher.einrichtung`).
 *
 * ═══ WARUM DIESE DATEI ═════════════════════════════════════════════════════
 * Das Formular in der Handbuch-Uebersicht bestand aus fuenf leeren Textfeldern
 * mit Beispielen im Platzhalter. Wer es zum ersten Mal oeffnete, wusste weder
 * WAS hineingehoert noch WIE VIEL — die Felder landen in Kapitel 3 in zwei
 * schmalen Spalten mit `overflow: hidden`, und was dort nicht hinpasst, faellt
 * beim Druck ab, ohne dass es jemand meldet.
 *
 * Hier stehen deshalb drei Dinge, die alle drei pure Funktionen bzw. Tabellen
 * sind (stehende Entscheidung im Repo: duenner Container, Logik daneben — das
 * Projekt uebersetzt seine Tests ohne jsdom, eine Regel ist nur dann geprueft,
 * wenn sie AUSSERHALB einer Komponente liegt):
 *
 *   1. BAUSTEINE. Anklickbare Saetze je Feld. Sie ergaenzen den Text und
 *      bleiben danach frei editierbar — eine Schreibhilfe, keine Vorgabe.
 *   2. REGELWERK. `einrichtungsVorschlag` leitet aus den Gewerken und
 *      gemeinsamen Einrichtungen des SiGe-Plans einen Vorschlag je Feld ab.
 *   3. LAENGENMASS. `druckZeilen` schaetzt, wie viele Zeilen ein Text in der
 *      Kapitelspalte belegt, damit die Oberflaeche vor dem Abschneiden warnt.
 *
 * ═══ WAS HIER AUSDRUECKLICH NICHT PASSIERT ═════════════════════════════════
 * Nichts davon schreibt. Ein Vorschlag wird angeboten, in den ENTWURF
 * uebernommen und erst durch den sichtbaren Balken „Speichern/Verwerfen"
 * dauerhaft — derselbe Weg, den der KI-Assistent fuer die Baustellenordnung
 * geht. Eine Vorbelegung, die sich still einschreibt, stuende beim naechsten
 * Druck im Handbuch, ohne dass sie jemand entschieden hat; im Handbuchmodul
 * ist das eine stehende Entscheidung („Vorbelegung nie einschreiben").
 *
 * Rein: kein DOM, kein React, kein Supabase, kein `fetch`.
 */

import { gewerkAusText, UNBEKANNT } from '../koordinierung/gewerkeKatalog';
import type { GewerkId, GewerkTreffer } from '../koordinierung/gewerkeKatalog';
import type { Baustelleneinrichtung } from './types';

/** Die fuenf Felder von `handbuecher.einrichtung`. */
export type EinrichtungsFeld = keyof Baustelleneinrichtung;

// ═══════════════════════════════════════════════════════════════════════════
// 1) DIE FELDER — was hineingehoert und wie viel Platz das Kapitel hat
// ═══════════════════════════════════════════════════════════════════════════

export interface FeldBeschreibung {
    feld: EinrichtungsFeld;
    label: string;
    /**
     * Ein Satz, der die Frage beantwortet „was schreibe ich hier hinein?".
     *
     * Bewusst eine FRAGE und kein Beispiel: der Platzhalter zeigte bisher eine
     * Aufzaehlung („Baustrom, Bauwasser, Beleuchtung, Sanitaer …"), und die
     * las sich wie eine Vorgabe, die man abarbeitet. Gefragt ist aber eine
     * Festlegung — wer stellt es, wo steht es, wer prueft es.
     */
    merksatz: string;
    /** Wo der Text im gedruckten Handbuch landet. Steht unter dem Feld. */
    ziel: string;
    /** Zeilen im Textfeld der Oberflaeche. */
    zeilen: number;
    /** Ueber die volle Formularbreite (nur die Bemerkung). */
    breit?: boolean;
    /**
     * Wie viele Zeilen dieses Feld in der Kapitelspalte hat, bevor es
     * abgeschnitten wird. Naeherung, siehe `druckZeilen`.
     */
    druckZeilen: number;
}

/**
 * Die Felder in der Reihenfolge, in der ein Koordinator sie aufschreibt.
 *
 * Die Zeilenzahlen unter `druckZeilen` sind aus dem Kapitelblatt abgelesen:
 * die untere Haelfte von `Baustelleneinrichtung.tsx` bekommt hoechstens 32 %
 * der A3-Hoehe, darin stehen links drei Angaben (Zugang, Verkehrsfuehrung,
 * Lagerplatz) und rechts zwei plus die Bemerkung. Beide Spalten tragen bei
 * 8,5 px und Zeilenhoehe 1,5 rund 19 Zeilen — geteilt durch die Angaben und
 * konservativ abgerundet, weil die Zugangsregelung aus der Baustellenordnung
 * unbekannter Laenge dazwischensteht.
 */
export const EINRICHTUNGSFELDER: FeldBeschreibung[] = [
    {
        feld: 'versorgung',
        label: 'Versorgung',
        merksatz: 'Wer stellt Strom, Wasser, Licht und Sanitär — und wer prüft es?',
        ziel: 'Kapitel 3, Spalte „Ver- und Entsorgung"',
        zeilen: 3,
        druckZeilen: 6,
    },
    {
        feld: 'entsorgung',
        label: 'Entsorgung',
        merksatz: 'Wohin mit Bauschutt, Restmengen und Abwasser — und wer räumt?',
        ziel: 'Kapitel 3, Spalte „Ver- und Entsorgung"',
        zeilen: 3,
        druckZeilen: 6,
    },
    {
        feld: 'lagerplatz',
        label: 'Lagerflächen',
        merksatz: 'Wer darf wo lagern, und welche Flächen bleiben frei?',
        ziel: 'Kapitel 3, Spalte „Verkehrswege & Zugang"',
        zeilen: 3,
        druckZeilen: 5,
    },
    {
        feld: 'verkehrsfuehrung',
        label: 'Verkehrsführung',
        merksatz: 'Wie wird zugefahren, und wie bleiben Rettungsweg und Zufahrt frei?',
        ziel: 'Kapitel 3, Spalte „Verkehrswege & Zugang"',
        zeilen: 3,
        druckZeilen: 5,
    },
    {
        feld: 'bemerkung',
        label: 'Bemerkung',
        merksatz: 'Was sonst zum Baustelleneinrichtungsplan zu sagen ist — ohne Wiederholung.',
        ziel: 'Kapitel 3, unter der Ver- und Entsorgung',
        zeilen: 2,
        breit: true,
        druckZeilen: 4,
    },
];

// ═══════════════════════════════════════════════════════════════════════════
// 2) DAS LAENGENMASS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Zeichen, die in eine Zeile der Kapitelspalte passen.
 *
 * Naeherung und ausdruecklich keine Messung: die Seite ist hoechstens 1200 px
 * breit, die untere Reihe teilt sie in drei Abschnitte, und bei 8,5 px
 * Schriftgroesse traegt eine Spalte rund 78 Zeichen. Genauer ginge es nur mit
 * einer echten Textmessung im Browser — die gaebe es hier nicht zu pruefen,
 * und fuer den Zweck („bleib ungefaehr in diesem Rahmen") reicht die Zahl.
 */
export const DRUCK_ZEICHEN_JE_ZEILE = 78;

/**
 * Wie viele Zeilen ein Text in der Kapitelspalte belegt.
 *
 * Zeilenumbrueche im Text zaehlen als eigene Zeile — im Kapitel steht
 * `whiteSpace: 'pre-wrap'`, dort bleiben sie erhalten.
 *
 * `labelZeichen` zieht die fettgedruckte Bezeichnung ab, die im Kapitel VOR
 * dem Text in derselben Zeile steht („Versorgung: …"). Ohne diesen Abzug
 * meldete die Anzeige Platz, den es nicht gibt.
 */
export const druckZeilen = (text: string, labelZeichen = 0): number => {
    const inhalt = (text || '').replace(/\r/g, '');
    if (!inhalt.trim()) return 0;
    return inhalt.split('\n').reduce((summe, zeile, i) => {
        const laenge = zeile.length + (i === 0 ? labelZeichen : 0);
        return summe + Math.max(1, Math.ceil(laenge / DRUCK_ZEICHEN_JE_ZEILE));
    }, 0);
};

// ═══════════════════════════════════════════════════════════════════════════
// 3) DIE BAUSTEINE
// ═══════════════════════════════════════════════════════════════════════════

export interface Baustein {
    /** Stabiler Schluessel — die Oberflaeche merkt sich daran, was schon steht. */
    id: string;
    feld: EinrichtungsFeld;
    /** Kurzer Knopftext. */
    kurz: string;
    /** Was eingefuegt wird. */
    text: string;
}

/**
 * Die anklickbaren Textbausteine je Feld.
 *
 * Sie sind bewusst VOLLSTAENDIGE Saetze und keine Stichworte: „Baustrom" auf
 * dem Blatt ist keine Festlegung, „Baustrom ueber den Verteiler am Container,
 * Pruefung durch den AN Elektro" ist eine. Wer es anders haben will,
 * ueberschreibt den Satz — das Feld bleibt ein Textfeld.
 *
 * Die Fundstellen (ASR, DGUV, RSA) stehen mit im Satz. Sie sind der Grund,
 * warum ein Baustein ueberhaupt so lautet, und im Streitfall genau die
 * Angabe, die auf dem Blatt fehlt.
 */
export const BAUSTEINE: Baustein[] = [
    // ─── Versorgung ──────────────────────────────────────────────────────
    {
        id: 'v-baustrom', feld: 'versorgung', kurz: 'Baustrom',
        text: 'Baustrom über Baustromverteiler mit RCD; Aufbau, Betrieb und '
            + 'wiederkehrende Prüfung nach DGUV Information 203-006 durch das Elektrogewerk.',
    },
    {
        id: 'v-bauwasser', feld: 'versorgung', kurz: 'Bauwasser',
        text: 'Bauwasser über den Standrohranschluss laut BE-Plan; Frostsicherung '
            + 'und Rückbau durch das entnehmende Gewerk.',
    },
    {
        id: 'v-beleuchtung', feld: 'versorgung', kurz: 'Beleuchtung',
        text: 'Verkehrswege und Arbeitsplätze werden nach ASR A3.4 beleuchtet; in engen '
            + 'leitfähigen Räumen nur Leuchten mit Schutzkleinspannung.',
    },
    {
        id: 'v-sanitaer', feld: 'versorgung', kurz: 'Sanitär',
        text: 'Sanitärräume, Waschgelegenheit und Tagesunterkunft nach ASR A4.1 stellt der '
            + 'Auftraggeber; die Reinigung ist geregelt.',
    },
    {
        id: 'v-erstehilfe', feld: 'versorgung', kurz: 'Erste Hilfe',
        text: 'Verbandkasten und Meldeeinrichtung befinden sich im Baubüro; der Aushang '
            + 'Erste Hilfe hängt an der Sammelstelle.',
    },

    // ─── Entsorgung ──────────────────────────────────────────────────────
    {
        id: 'e-container', feld: 'entsorgung', kurz: 'Container',
        text: 'Abfälle werden getrennt in den Containern auf der Fläche laut BE-Plan '
            + 'gesammelt; jedes Gewerk entsorgt seinen eigenen Abfall.',
    },
    {
        id: 'e-bauschutt', feld: 'entsorgung', kurz: 'Bauschutt',
        text: 'Bauschutt und Aushub werden getrennt gehalten; eine Vermischung macht die '
            + 'gesamte Charge entsorgungspflichtig.',
    },
    {
        id: 'e-gefahrstoff', feld: 'entsorgung', kurz: 'Gefährlicher Abfall',
        text: 'Gefährliche Abfälle (Farben, Kleber, Dichtstoffe, ölhaltige Betriebsmittel) '
            + 'werden getrennt gesammelt, gekennzeichnet und mit Nachweis entsorgt.',
    },
    {
        id: 'e-abwasser', feld: 'entsorgung', kurz: 'Abwasser',
        text: 'Bauabwasser wird vor der Einleitung abgesetzt; Zement- und Schlämmwasser '
            + 'gelangt nicht in die Kanalisation oder das Erdreich.',
    },
    {
        id: 'e-taeglich', feld: 'entsorgung', kurz: 'Tägliches Räumen',
        text: 'Jedes Gewerk räumt seinen Arbeitsbereich täglich; Verkehrswege und '
            + 'Fluchtwege bleiben dabei frei.',
    },

    // ─── Lagerflächen ────────────────────────────────────────────────────
    {
        id: 'l-zuweisung', feld: 'lagerplatz', kurz: 'Zuweisung',
        text: 'Gelagert wird nur auf den im BE-Plan ausgewiesenen Flächen; die Zuweisung '
            + 'an die Gewerke erfolgt über die Bauleitung.',
    },
    {
        id: 'l-freihalten', feld: 'lagerplatz', kurz: 'Freizuhalten',
        text: 'Rettungswege, Feuerwehrzufahrten, Hydranten und Verteiler sind von '
            + 'Lagergut freizuhalten.',
    },
    {
        id: 'l-standsicher', feld: 'lagerplatz', kurz: 'Standsicherheit',
        text: 'Lagergut wird standsicher und gegen Verrutschen gesichert gestapelt; '
            + 'Stapelhöhen richten sich nach der Herstellerangabe.',
    },
    {
        id: 'l-decken', feld: 'lagerplatz', kurz: 'Deckenlast',
        text: 'Lagerung auf Decken nur nach Freigabe der Bauleitung; die zulässige '
            + 'Deckenlast ist einzuhalten.',
    },
    {
        id: 'l-gefahrstoff', feld: 'lagerplatz', kurz: 'Gefahrstoffe',
        text: 'Gefahrstoffe und Druckgasflaschen lagern getrennt, gekennzeichnet und '
            + 'gegen unbefugten Zugriff gesichert.',
    },

    // ─── Verkehrsführung ─────────────────────────────────────────────────
    {
        id: 'f-zufahrt', feld: 'verkehrsfuehrung', kurz: 'Zufahrt',
        text: 'Die Zufahrt erfolgt über die im BE-Plan eingezeichnete Baustraße; auf dem '
            + 'gesamten Gelände gilt Schrittgeschwindigkeit.',
    },
    {
        id: 'f-rettungsweg', feld: 'verkehrsfuehrung', kurz: 'Rettungsweg',
        text: 'Feuerwehrzufahrt und Rettungsweg bleiben in mindestens 3,5 m Breite '
            + 'jederzeit frei.',
    },
    {
        id: 'f-trennung', feld: 'verkehrsfuehrung', kurz: 'Fuß- und Fahrverkehr',
        text: 'Fuß- und Fahrverkehr sind getrennt geführt; wo das nicht möglich ist, '
            + 'sichern Absperrung und Einweiser.',
    },
    {
        id: 'f-rueckwaerts', feld: 'verkehrsfuehrung', kurz: 'Rückwärtsfahren',
        text: 'Rückwärtsfahren nur mit Einweiser; die Verkehrsführung ist möglichst als '
            + 'Einbahnregelung angelegt.',
    },
    {
        id: 'f-anlieferung', feld: 'verkehrsfuehrung', kurz: 'Anlieferung',
        text: 'Anlieferungen werden bei der Bauleitung angemeldet; Fahrzeuge halten nur '
            + 'auf den ausgewiesenen Flächen.',
    },

    // ─── Bemerkung ───────────────────────────────────────────────────────
    {
        id: 'b-fortschreibung', feld: 'bemerkung', kurz: 'Fortschreibung',
        text: 'Der Baustelleneinrichtungsplan wird bei wesentlichen Änderungen '
            + 'fortgeschrieben; die Änderung wird den Gewerken bekannt gegeben.',
    },
    {
        id: 'b-nachbarschaft', feld: 'bemerkung', kurz: 'Nachbarschaft',
        text: 'Zum angrenzenden öffentlichen Verkehrsraum besteht eine Absicherung; '
            + 'Verschmutzungen der Straße werden umgehend beseitigt.',
    },
    {
        id: 'b-bauzaun', feld: 'bemerkung', kurz: 'Bauzaun',
        text: 'Die Baustelle ist umwehrt und gegen unbefugtes Betreten gesichert; '
            + 'Tore werden außerhalb der Arbeitszeit verschlossen.',
    },
];

/** Die Bausteine eines Feldes, in Katalogreihenfolge. */
export const bausteineZuFeld = (feld: EinrichtungsFeld): Baustein[] =>
    BAUSTEINE.filter(b => b.feld === feld);

/**
 * Haengt einen Baustein an einen vorhandenen Text an.
 *
 * Steht der Satz bereits da, bleibt der Text UNVERAENDERT zurueck — sonst
 * verdoppelte ein zweiter Klick die Zeile, und auf dem Blatt staende alles
 * zweimal. Verglichen wird auf zusammengefallene Leerzeichen, damit ein von
 * Hand umgebrochener Satz nicht als neu gilt.
 */
export const fuegeBausteinEin = (vorhanden: string, baustein: Baustein): string => {
    const flach = (s: string) => s.replace(/\s+/g, ' ').trim().toLowerCase();
    const alt = vorhanden || '';
    if (flach(alt).includes(flach(baustein.text))) return alt;
    if (!alt.trim()) return baustein.text;
    return `${alt.replace(/\s+$/, '')}\n${baustein.text}`;
};

// ═══════════════════════════════════════════════════════════════════════════
// 4) DAS REGELWERK
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Die Lage, aus der ein Vorschlag entsteht.
 *
 * Eine eigene, schmale Form und ausdruecklich nicht `KapitelQuelle`: die
 * Regel soll ohne den halben Planzweig pruefbar bleiben — dieselbe
 * Ueberlegung wie bei `EinrichtungsSchritt` in `handbuchLogic.ts`. Der
 * Aufrufer schreibt Feld fuer Feld ab, was er hat.
 */
export interface EinrichtungsLage {
    /** Die Gewerke der Planfassung. `katalogId` darf `null` sein. */
    gewerke: { katalogId?: string | null; label?: string | null }[];
    /** Die gemeinsam genutzten Einrichtungen aus `gemeinsameEinrichtungen`. */
    einrichtungen: { bezeichnung: string; mitnutzer: string[] }[];
    /** `ordnung.sammelplatz` — wird zitiert, nicht erfunden. */
    sammelplatz?: string | null;
    /** Liegt im Projekt mindestens ein Lageplan (`metadata.plans`)? */
    lageplanVorhanden: boolean;
    /**
     * Sind die Planquellen ueberhaupt geladen?
     *
     * Der Unterschied zaehlt: „der Plan hat keine Gewerke" und „der Plan ist
     * noch nicht geladen" sehen in den Daten gleich aus, meinen aber
     * Gegenteiliges. Ohne diese Flagge meldete der Vorschlag eine leere
     * Baustelle, wo bloss noch keine Abfrage gelaufen ist.
     */
    quellenGeladen: boolean;
}

/** Ein vorgeschlagener Satz mit seiner Herkunft. */
export interface VorschlagZeile {
    feld: EinrichtungsFeld;
    text: string;
    /** Warum dieser Satz vorgeschlagen wird. Steht in der Oberflaeche. */
    grund: string;
}

export interface EinrichtungsVorschlag {
    zeilen: VorschlagZeile[];
    /**
     * Was das Regelwerk NICHT beantworten kann, im Klartext.
     *
     * Diese Liste ist der Grund, warum der Vorschlag ueberhaupt vertretbar
     * ist: ein Werkzeug, das aus duenner Datenlage einen vollstaendig
     * aussehenden Text erzeugt, erzeugt eine Falschaussage. Hier steht
     * daneben, worauf er sich stuetzt und worauf nicht.
     */
    offen: string[];
    /** Keine Planquellen geladen — der Vorschlag beruht nur auf Grundregeln. */
    ohneGrundlage: boolean;
}

interface Regel {
    feld: EinrichtungsFeld;
    /**
     * Gewerke, von denen mindestens eines im Plan stehen muss. Fehlt die
     * Angabe, gilt die Regel immer — das sind die Saetze, die auf jeder
     * Baustelle gelten (Rettungsweg, taegliches Raeumen).
     */
    gewerke?: GewerkId[];
    text: string;
    grund: string;
}

/**
 * Die Regeln, ausgewertet in dieser Reihenfolge.
 *
 * Sie sind bewusst duenn: was hier steht, muss sich aus dem Plan BELEGEN
 * lassen. Der Vorschlag soll dem Koordinator die immer gleichen Saetze
 * abnehmen und ihm keine Baustelle beschreiben, die niemand gesehen hat —
 * deshalb keine Regel zu Bodenverhaeltnissen, Nachbarbebauung oder
 * Anschlusspunkten. Dafuer sind die Bausteine und das Textfeld da.
 */
const REGELN: Regel[] = [
    // ─── Versorgung ──────────────────────────────────────────────────────
    {
        feld: 'versorgung', gewerke: ['elektro'],
        text: 'Baustrom über Baustromverteiler mit RCD; Aufbau, Betrieb und wiederkehrende '
            + 'Prüfung nach DGUV Information 203-006 durch das Elektrogewerk.',
        grund: 'Das Elektrogewerk steht im Plan.',
    },
    {
        feld: 'versorgung', gewerke: ['shk', 'sprinkler'],
        text: 'Bauwasser über den Anschluss des Gewerks Sanitär/Heizung/Klima; '
            + 'Frostsicherung und Rückbau durch das entnehmende Gewerk.',
        grund: 'Ein wasserführendes Gewerk (SHK/Sprinkler) steht im Plan.',
    },
    {
        feld: 'versorgung',
        text: 'Sanitärräume, Waschgelegenheit und Tagesunterkunft nach ASR A4.1 sind '
            + 'gestellt; die Reinigung ist geregelt.',
        grund: 'Gilt auf jeder Baustelle (ASR A4.1).',
    },

    // ─── Entsorgung ──────────────────────────────────────────────────────
    {
        feld: 'entsorgung', gewerke: ['rohbau', 'abbruch', 'erdbau', 'spezialtiefbau'],
        text: 'Bauschutt und Aushub werden getrennt in Containern auf der Fläche laut '
            + 'BE-Plan gesammelt; die Abfuhr veranlasst das verursachende Gewerk.',
        grund: 'Schuttintensive Gewerke (Rohbau, Abbruch, Erd- oder Spezialtiefbau) stehen im Plan.',
    },
    {
        feld: 'entsorgung', gewerke: ['schadstoff'],
        text: 'Schadstoffhaltige Abfälle werden getrennt gesammelt, gekennzeichnet und mit '
            + 'Entsorgungsnachweis abgefahren; eine Vermischung mit Bauschutt ist untersagt.',
        grund: 'Eine Schadstoffsanierung steht im Plan — die Entsorgung ist nachweispflichtig.',
    },
    {
        feld: 'entsorgung', gewerke: ['maler', 'trockenbau', 'estrich', 'fliesenleger'],
        text: 'Restmengen von Farben, Klebern und Spachtelmassen gelten als gefährlicher '
            + 'Abfall und gehören nicht in den Bauschuttcontainer.',
        grund: 'Ausbaugewerke mit Restmengen (Maler, Trockenbau, Estrich, Fliesen) stehen im Plan.',
    },
    {
        feld: 'entsorgung',
        text: 'Jedes Gewerk räumt seinen Arbeitsbereich täglich; Verkehrswege und '
            + 'Fluchtwege bleiben dabei frei.',
        grund: 'Gilt auf jeder Baustelle.',
    },

    // ─── Lagerflächen ────────────────────────────────────────────────────
    {
        feld: 'lagerplatz', gewerke: ['geruestbau'],
        text: 'Gerüstbauteile werden nur auf den ausgewiesenen Flächen gelagert und gegen '
            + 'Umfallen gesichert.',
        grund: 'Der Gerüstbau steht im Plan.',
    },
    {
        feld: 'lagerplatz', gewerke: ['kran'],
        text: 'Unter dem Schwenkbereich des Krans wird nicht gelagert, solange Lasten '
            + 'geführt werden.',
        grund: 'Ein Kranbetrieb steht im Plan.',
    },
    {
        feld: 'lagerplatz', gewerke: ['schadstoff'],
        text: 'Gefahrstoffe und schadstoffhaltiges Material lagern getrennt, gekennzeichnet '
            + 'und gegen unbefugten Zugriff gesichert.',
        grund: 'Eine Schadstoffsanierung steht im Plan.',
    },
    {
        feld: 'lagerplatz',
        text: 'Rettungswege, Feuerwehrzufahrten, Hydranten und Verteiler sind von Lagergut '
            + 'freizuhalten.',
        grund: 'Gilt auf jeder Baustelle.',
    },

    // ─── Verkehrsführung ─────────────────────────────────────────────────
    {
        feld: 'verkehrsfuehrung', gewerke: ['kran'],
        text: 'Die Kranstellfläche ist abgesperrt; Lasten werden nicht über Verkehrswege '
            + 'geführt, solange diese begangen oder befahren werden.',
        grund: 'Ein Kranbetrieb steht im Plan.',
    },
    {
        feld: 'verkehrsfuehrung', gewerke: ['strassenbau', 'erdbau'],
        text: 'Im öffentlichen Verkehrsraum gilt eine Verkehrsführung nach RSA 21; die '
            + 'Absperrung stellt und kontrolliert das ausführende Gewerk.',
        grund: 'Straßen- oder Erdbau steht im Plan — Arbeiten reichen erfahrungsgemäß in den Verkehrsraum.',
    },
    {
        feld: 'verkehrsfuehrung',
        text: 'Die Zufahrt erfolgt über die im BE-Plan eingezeichnete Baustraße; auf dem '
            + 'gesamten Gelände gilt Schrittgeschwindigkeit.',
        grund: 'Gilt auf jeder Baustelle.',
    },
    {
        feld: 'verkehrsfuehrung',
        text: 'Feuerwehrzufahrt und Rettungsweg bleiben in mindestens 3,5 m Breite '
            + 'jederzeit frei.',
        grund: 'Gilt auf jeder Baustelle; steht so auch im Hinweiskasten des Kapitels.',
    },
];

/** Erkennt das Gewerk einer Planzeile — Katalogtreffer vor Freitext. */
const gewerkTreffer = (zeile: { katalogId?: string | null; label?: string | null }): GewerkTreffer => {
    // `katalogId` ist im Handbuch bereits zugeordnet; sie hat Vorrang. Erst
    // wenn sie fehlt, wird der Freitext gelesen — und zwar mit `gewerkAusText`
    // und nicht mit einem eigenen Abgleich: zwei Zuordnungswege liefen
    // irgendwann auseinander, und dann widerspraeche der Vorschlag der
    // Gewerkeseite desselben Handbuchs.
    const ausId = (zeile.katalogId || '').trim();
    if (ausId) return ausId as GewerkTreffer;
    return gewerkAusText(zeile.label);
};

/**
 * Der Vorschlag zur Baustelleneinrichtung.
 *
 * Liefert Saetze je Feld MIT Begruendung und daneben die Liste dessen, was er
 * nicht wissen kann. Uebernommen wird nichts — das entscheidet der Aufrufer,
 * und gespeichert wird erst ueber den Entwurfsbalken.
 */
export const einrichtungsVorschlag = (lage: EinrichtungsLage): EinrichtungsVorschlag => {
    const vorhandene = new Set<GewerkTreffer>(
        (lage.gewerke || []).map(gewerkTreffer).filter(t => t && t !== UNBEKANNT),
    );
    const unbekannte = (lage.gewerke || []).filter(g => gewerkTreffer(g) === UNBEKANNT).length;

    const zeilen: VorschlagZeile[] = REGELN
        .filter(r => !r.gewerke || r.gewerke.some(g => vorhandene.has(g)))
        .map(r => ({ feld: r.feld, text: r.text, grund: r.grund }));

    // Die gemeinsam genutzten Einrichtungen des Plans namentlich — sie sind
    // der einzige Teil des Vorschlags, der wirklich AUS DIESEM PROJEKT kommt.
    const lagerEinrichtungen = (lage.einrichtungen || [])
        .filter(e => /lager|stellflaeche|stellfläche|material/i.test(e.bezeichnung));
    if (lagerEinrichtungen.length) {
        zeilen.push({
            feld: 'lagerplatz',
            text: `Die im SiGe-Plan geführten Flächen (${lagerEinrichtungen.map(e => e.bezeichnung).join(', ')}) `
                + 'werden gemeinsam genutzt; die Zuweisung an die Gewerke erfolgt über die Bauleitung.',
            grund: 'Steht als gemeinsam genutzte Einrichtung im SiGe-Plan.',
        });
    }

    const wegEinrichtungen = (lage.einrichtungen || [])
        .filter(e => /verkehr|weg|zufahrt|gerüst|geruest|aufzug/i.test(e.bezeichnung));
    if (wegEinrichtungen.length) {
        zeilen.push({
            feld: 'verkehrsfuehrung',
            text: `Gemeinsam genutzt werden ${wegEinrichtungen.map(e => e.bezeichnung).join(', ')}; `
                + 'Änderungen daran werden vorher mit der Bauleitung abgestimmt.',
            grund: 'Steht als gemeinsam genutzte Einrichtung im SiGe-Plan.',
        });
    }

    if (lage.sammelplatz?.trim()) {
        zeilen.push({
            feld: 'verkehrsfuehrung',
            text: `Der Weg zum Sammelplatz (${lage.sammelplatz.trim()}) ist gekennzeichnet und freizuhalten.`,
            grund: 'Der Sammelplatz steht in der Baustellenordnung dieses Handbuchs.',
        });
    }

    // ─── Was der Vorschlag NICHT weiss ───────────────────────────────────
    const offen: string[] = [];
    if (!lage.quellenGeladen) {
        offen.push('Die Plandaten sind nicht geladen. Der Vorschlag beruht allein auf den '
            + 'Regeln, die auf jeder Baustelle gelten — kein Gewerk dieses Projekts ist eingeflossen.');
    } else if (!vorhandene.size) {
        offen.push('Im SiGe-Plan ist kein Gewerk erkannt worden. Gewerkbezogene Vorschläge '
            + '(Baustrom, Bauschutt, Kran, Verkehrsraum) fehlen deshalb.');
    }
    if (unbekannte > 0) {
        offen.push(`${unbekannte} Gewerk(e) des Plans ließen sich keinem Katalogeintrag zuordnen; `
            + 'zu ihnen schlägt das Regelwerk nichts vor.');
    }
    if (!lage.lageplanVorhanden) {
        offen.push('Es ist kein Lageplan hinterlegt. Zufahrt, Container- und Lagerflächen '
            + 'lassen sich deshalb nur allgemein beschreiben und nicht verorten.');
    }
    if (!lage.sammelplatz?.trim()) {
        offen.push('Der Sammelplatz ist in der Baustellenordnung nicht erfasst; die '
            + 'Verkehrsführung kann ihn deshalb nicht benennen.');
    }
    offen.push('Anschlusspunkte, Nachbarbebauung, Bodenverhältnisse und die tatsächlichen '
        + 'Standorte kennt nur, wer die Baustelle gesehen hat. Der Vorschlag ist ein Entwurf '
        + 'zum Gegenlesen und keine Bestandsaufnahme.');

    return { zeilen, offen, ohneGrundlage: !lage.quellenGeladen };
};

/**
 * Fuehrt einen Vorschlag mit dem vorhandenen Text zusammen.
 *
 * VORHANDENES BLEIBT STEHEN und wird nur ergaenzt — ein Vorschlag, der einen
 * von Hand geschriebenen Absatz ueberschreibt, ist ein Datenverlust, den
 * niemand rueckgaengig machen kann, sobald der Balken gespeichert hat. Doppelte
 * Saetze filtert `fuegeBausteinEin` ueber denselben Vergleich weg.
 */
export const wendeVorschlagAn = (
    bestand: Baustelleneinrichtung,
    vorschlag: EinrichtungsVorschlag,
): Baustelleneinrichtung => {
    const neu: Baustelleneinrichtung = { ...bestand };
    for (const zeile of vorschlag.zeilen) {
        neu[zeile.feld] = fuegeBausteinEin(neu[zeile.feld] || '', {
            id: '', feld: zeile.feld, kurz: '', text: zeile.text,
        });
    }
    return neu;
};
